import { IArrival } from '@/coreDomainsModule/entities/arrival';
import { IBusiness } from '@/coreDomainsModule/entities/businessBase';
import { IAnnounce } from '@/coreDomainsModule/entities/forecast';
import { ISalesResult } from '@/coreDomainsModule/entities/salesResults';
import { ArrivalFactory } from '@/coreDomainsModule/factories/ArrivalFactory';
import { BusinessFactory } from '@/coreDomainsModule/factories/BusinessFactory';
import { DateService } from '@/coreDomainsModule/services/dateService';
import {
  GetTrashBinAggregateBusinessIncludedBusinessSuccessResponseInfoAllOfResult,
  GetTrashBinAggregateBusinessSuccessResponseInfoAllOfResult,
  GetTrashBinBusinessSuccessResponseInfoAllOfResult,
  Inventory,
} from '@/coreInfrastructureModule/axios/generated/trashBinManage';
import { IAggregateBusiness } from '../domains/entities/aggregateBusiness';
import { UserAccount } from '@/coreDomainsModule/entities/users';
import { UserAccountFactory } from '@/coreDomainsModule/factories/userAccountFactory';

/**
 * ゴミ箱のadapter
 */
export class TrashBinAdapter {
  /**
   * Inventory[]からnumberへ変換する
   * 在庫年月が最新の在庫情報を取得する(なければ0を返す)
   * @param response - Order
   */
  private static adaptInventory = (response?: Inventory[]): number => {
    if (!response) return 0;

    const latestInventory = response.reduce((a, b) => {
      if (a.targetYear === undefined || a.targetMonth === undefined || b.targetYear === undefined || b.targetMonth === undefined) return a;
      return a.targetYear > b.targetYear || (a.targetYear === b.targetYear && a.targetMonth > b.targetMonth) ? a : b;
    });

    return latestInventory.inventoryAmount ?? 0;
  };

  /**
   * GetTrashBinAggregateBusinessSuccessResponseInfoAllOfResultをIBusiness[]に変換する
   * @param info - GetTrashBinAggregateBusinessSuccessResponseInfoAllOfResult
   */
  public static adaptAggregateBusiness = (info: GetTrashBinAggregateBusinessSuccessResponseInfoAllOfResult): Array<IAggregateBusiness> => {
    const result: Array<IAggregateBusiness> = [];

    info.data?.forEach((aggregateBusiness) => {
      const business = BusinessFactory.create({
        businessName: aggregateBusiness.businessName,
        warehouseNumber: aggregateBusiness.warehouseNumber,
        warehouse: aggregateBusiness.warehouse,
        supplierNumber: aggregateBusiness.supplierNumber,
        supplierName: aggregateBusiness.supplierName,
        businessNumber: aggregateBusiness.businessNumber,
        updatedAt: aggregateBusiness.updateAt,
        package: aggregateBusiness.package?.addComma(),
        unit: aggregateBusiness.unit,
        manufacturer: aggregateBusiness.manufacturer,
        inventory: this.adaptInventory(aggregateBusiness.inventories),
        aggregateBusinessResponsibleUsers: aggregateBusiness.aggregateBusinessResponsibleUsers?.map((user) =>
          UserAccountFactory.create({ email: user.id, name: user.name })
        ),
        executionDate: DateService.convertUnixTimeToDate(aggregateBusiness.executionDate ? aggregateBusiness.executionDate / 1000 : 0),
        arrivals: aggregateBusiness.arrivals?.map((arrival): IArrival => {
          return ArrivalFactory.create({
            arrivalId: arrival.arrivalId,
            amount: arrival.arrivalAmount ?? 0,
            year: arrival.targetYear ?? 0,
            month: arrival.targetMonth ?? 0,
          });
        }),
        salesResults: aggregateBusiness.salesResults?.map((salesResult): ISalesResult => {
          return {
            amount: salesResult.salesResultAmount ?? 0,
            year: salesResult.targetYear ?? 0,
            month: salesResult.targetMonth ?? 0,
          };
        }),
        customerAnnounce: aggregateBusiness.customerAnnounces?.map((announce): IAnnounce => {
          return {
            amount: announce.customerForecastAmount ?? 0,
            manageInitiatedYearMonth: {
              year: announce.customerForecastReceivedYear ?? 0,
              month: announce.customerForecastReceivedMonth ?? 0,
            },
            year: announce.customerForecastTargetYear ?? 0,
            month: announce.customerForecastTargetMonth ?? 0,
            createAt: undefined,
            updateAt: undefined,
          };
        }),
      });
      result.push({
        ...business,
        ...{
          aggregateBusinessId: aggregateBusiness.aggregateBusinessId ?? '',
          children: [],
          childrenAsyncStatus: 'none',
          aggregateGroup: [],
        },
      });
    });
    return result;
  };

  /**
   * GetTrashBinAggregateBusinessIncludedBusinessSuccessResponseInfoAllOfResultをIBusiness[]に変換する
   * @param info - GetTrashBinAggregateBusinessIncludedBusinessSuccessResponseInfoAllOfResult
   */
  public static adaptAggregateBusinessIncludeBusiness = (
    info: GetTrashBinAggregateBusinessIncludedBusinessSuccessResponseInfoAllOfResult
  ): Array<IBusiness> => {
    const result: Array<IBusiness> = [];
    info.data?.forEach((business) => {
      const businessObj = BusinessFactory.create({
        businessId: business.businessId,
        customerNumber: business.customerNumber,
        customerName: business.customerName,
        salesDestinationNumber: business.salesDestinationNumber,
        salesDestinationName: business.salesDestinationName,
        endUser: business.endUser,
        updatedAt: business.updateAt,
        baseBusinessResponsibleUsers: business.baseBusinessResponsibleUsers?.map((user) =>
          UserAccountFactory.create({ email: user.id, name: user.name })
        ),
        salesResults: business.salesResults?.map((salesResult) => {
          return {
            amount: salesResult.salesResultAmount ?? 0,
            year: salesResult.targetYear ?? 0,
            month: salesResult.targetMonth ?? 0,
          };
        }),
        customerAnnounce: business.customerAnnounces?.map((announce) => {
          return {
            amount: announce.customerForecastAmount ?? 0,
            manageInitiatedYearMonth: {
              year: announce.customerForecastReceivedYear ?? 0,
              month: announce.customerForecastReceivedMonth ?? 0,
            },
            year: announce.customerForecastTargetYear ?? 0,
            month: announce.customerForecastTargetMonth ?? 0,
            createAt: undefined,
            updateAt: undefined,
          };
        }),
      });
      result.push(businessObj);
    });
    return result;
  };

  /**
   * GetTrashBinBusinessSuccessResponseInfoAllOfResultをIBusiness[]に変換する
   * @param info - GetTrashBinBusinessSuccessResponseInfoAllOfResult
   */
  public static adaptBusiness = (info: GetTrashBinBusinessSuccessResponseInfoAllOfResult): Array<IBusiness> => {
    const result: Array<IBusiness> = [];
    info.data?.forEach((business) => {
      const businessObj = BusinessFactory.create({
        businessId: business.businessId,
        businessNumber: business.businessNumber,
        businessName: business.businessName,
        warehouseNumber: business.warehouseNumber,
        warehouse: business.warehouse,
        supplierNumber: business.supplierNumber,
        supplierName: business.supplierName,
        unit: business.unit,
        manufacturer: business.manufacturer,
        updatedAt: business.updateAt,
        customerNumber: business.customerNumber,
        customerName: business.customerName,
        salesDestinationNumber: business.salesDestinationNumber,
        salesDestinationName: business.salesDestinationName,
        endUser: business.endUser,
        package: business.package?.addComma(),
        aggregateBusinessResponsibleUsers: business.aggregateBusinessResponsibleUsers?.map((user) =>
          UserAccountFactory.create({ email: user.id, name: user.name })
        ),
        baseBusinessResponsibleUsers: business.baseBusinessResponsibleUsers?.map((user) =>
          UserAccountFactory.create({ email: user.id, name: user.name })
        ),
        salesResults: business.salesResults?.map((salesResult) => {
          return {
            amount: salesResult.salesResultAmount ?? 0,
            year: salesResult.targetYear ?? 0,
            month: salesResult.targetMonth ?? 0,
          };
        }),
        customerAnnounce: business.customerAnnounces?.map((announce) => {
          return {
            amount: announce.customerForecastAmount ?? 0,
            manageInitiatedYearMonth: {
              year: announce.customerForecastReceivedYear ?? 0,
              month: announce.customerForecastReceivedMonth ?? 0,
            },
            year: announce.customerForecastTargetYear ?? 0,
            month: announce.customerForecastTargetMonth ?? 0,
            createAt: undefined,
            updateAt: undefined,
          };
        }),
      });
      result.push(businessObj);
    });
    return result;
  };
}
